import { Box, IconButton, InputAdornment, MenuItem, TextField, TextFieldProps, Typography } from '@mui/material';
import React from 'react';

import CAFlag from '../../assets/images/CanadaFlag.svg';
import USFlag from '../../assets/images/UsFlag.svg';
import { VisuallyHiddenStyles } from '../global-styles/GlobalStyles';
import { colors } from '../theme/theme';

import { TextFieldWrapper } from './text-field-styles';
import { TextFieldError, TextFieldTitle } from './TextFieldTitle';

interface CountryOption {
    value: string;
    label: string;
    flag: string;
}

interface PhoneInputProps extends Omit<TextFieldProps, 'variant' | 'size'> {
    id?: string;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    variant?: 'outlined' | 'filled' | 'standard';
    disabled?: boolean;
    required?: boolean;
    type?: string;
    placeholder?: string;
    error?: boolean;
    name?: string;
    value?: string;
    size?: 'medium' | 'small';
    isAutoFocus?: boolean;
    onBlur?: React.FocusEventHandler<HTMLInputElement>;
    maxLength?: number;
    className?: string;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    errorValue?: string;
    title?: string;
    helperTitle?: string;
    helperTitleOnClick?: () => void;
    sx?: Record<string, unknown>;
    countryCode?: string;
    onCountryChange?: (value: string) => void;
    countryOptions?: CountryOption[];
    label?: string;
    /** When true, shows the country code picker. Hidden by default. */
    showCountryPicker?: boolean;
}

const DEFAULT_COUNTRY_OPTIONS: CountryOption[] = [
    { value: '+1', label: 'United States (+1)', flag: USFlag },
    { value: '+1-ca', label: 'Canada (+1)', flag: CAFlag },
];

// Defined outside component to keep a stable reference — inline definitions cause the icon to
// remount on every render (React sees a new function type), which produces flickering.
const SelectDropdownIcon = (props: React.HTMLAttributes<HTMLSpanElement>) => (
    <span
        {...props}
        aria-hidden="true"
        className={['icon-arrow-drop-down', props.className].filter(Boolean).join(' ')}
        style={{ fontSize: 16, ...props.style }}
    />
);

export const PhoneInputComponent: React.FC<PhoneInputProps> = ({
    id,
    onChange,
    variant = 'outlined',
    disabled = false,
    required = false,
    type = 'text',
    placeholder = '',
    error = false,
    errorValue = '',
    name = '',
    value = '',
    size = 'medium',
    isAutoFocus = false,
    onBlur,
    maxLength,
    sx,
    startIcon,
    endIcon,
    title,
    helperTitle,
    label,
    helperTitleOnClick,
    countryCode = '+1',
    onCountryChange,
    countryOptions = DEFAULT_COUNTRY_OPTIONS,
    showCountryPicker = false,
}) => {
    const [showPassword, setShowPassword] = React.useState(false);

    const handleTogglePassword = () => setShowPassword(prev => !prev);

    // Handle onChange to allow only numbers
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;
        // Allow only numbers (0-9)
        const numericValue = inputValue.replace(/[^0-9]/g, '');

        // Create a new event with the filtered value
        const syntheticEvent = {
            ...e,
            target: {
                ...e.target,
                value: numericValue,
            },
        } as React.ChangeEvent<HTMLInputElement>;

        if (onChange) {
            onChange(syntheticEvent);
        }
    };

    // Prevent non-numeric key presses
    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const charCode = e.which || e.keyCode;
        // Allow: backspace, delete, tab, escape, enter, and numbers
        if (
            charCode === 8 || // backspace
            charCode === 9 || // tab
            charCode === 27 || // escape
            charCode === 13 || // enter
            charCode === 46 || // delete
            (charCode >= 48 && charCode <= 57) // 0-9
        ) {
            return;
        }
        e.preventDefault();
    };

    const getDialCode = (option: CountryOption) => {
        const match = option.label.match(/\(([^)]+)\)/);
        return match ? match[1] : option.value;
    };

    const renderCountrySelector = () => {
        const selectedCountry = countryOptions.find(option => option.value === countryCode) || countryOptions[0];
        const countryLabelId = id ? `${id}-country-label` : 'country-code-label';

        return (
            <>
                <VisuallyHiddenStyles id={countryLabelId} as="label">
                    Country code
                </VisuallyHiddenStyles>
                <TextField
                    select
                    value={selectedCountry.value}
                    variant="standard"
                    onChange={e => onCountryChange?.(e.target.value)}
                    disabled={disabled}
                    InputProps={{
                        disableUnderline: true,
                    }}
                    inputProps={{
                        'aria-labelledby': countryLabelId,
                    }}
                    SelectProps={{
                        IconComponent: SelectDropdownIcon,

                        renderValue: selected => {
                            const option = countryOptions.find(item => item.value === selected) || selectedCountry;
                            return (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px',
                                    }}
                                >
                                    <img
                                        src={option.flag}
                                        alt={`${option.label} flag`}
                                        width="24"
                                        style={{ borderRadius: '4px' }}
                                    />
                                    <Typography variant="body2" color="textSecondary">
                                        {getDialCode(option)}
                                    </Typography>
                                </Box>
                            );
                        },
                        MenuProps: {
                            PaperProps: {
                                sx: {
                                    backgroundColor: colors.fullWhite,
                                    borderRadius: 4,
                                    border: `1px solid ${colors.greyPreset[30]}`,
                                    '& .MuiList-root': {
                                        py: '14px',
                                        px: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1,
                                        '& .MuiButtonBase-root': {
                                            px: 2,
                                        },
                                    },
                                },
                            },
                        },
                    }}
                    sx={{
                        minWidth: '72px',
                        '& .MuiSelect-select': {
                            padding: '0 !important',
                            display: 'flex',
                            alignItems: 'center',
                        },
                    }}
                >
                    {countryOptions.map(option => (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}
                            aria-label={option.label}
                        >
                            <img src={option.flag} alt={`${option.label} flag`} width="24" />
                            <Typography variant="body2" fontWeight={600}>
                                {getDialCode(option)}
                            </Typography>
                        </MenuItem>
                    ))}
                </TextField>
            </>
        );
    };

    const renderEndAdornment = () => {
        if (type === 'password') {
            return (
                <InputAdornment position="end">
                    <IconButton onClick={handleTogglePassword} edge="end">
                        {showPassword ? (
                            <span className="icon-eye" style={{ fontSize: 16 }} />
                        ) : (
                            <span className="icon-eye-off" style={{ fontSize: 16 }} />
                        )}
                    </IconButton>
                </InputAdornment>
            );
        }

        if (endIcon) {
            return <InputAdornment position="end">{endIcon}</InputAdornment>;
        }

        return null;
    };

    const renderStartAdornment = () => {
        const startContent = startIcon ?? (showCountryPicker ? renderCountrySelector() : null);
        if (!startContent) return null;
        return <InputAdornment position="start">{startContent}</InputAdornment>;
    };

    return (
        <TextFieldWrapper>
            {title && <TextFieldTitle title={title} required={required} />}

            <TextField
                id={id}
                label={label || title}
                variant={variant}
                onChange={handlePhoneChange}
                onKeyPress={handleKeyPress}
                disabled={disabled}
                required={required}
                placeholder={placeholder}
                type={type === 'password' && showPassword ? 'text' : type}
                autoComplete="off"
                size={size}
                error={error}
                name={name}
                value={value}
                autoFocus={isAutoFocus}
                InputProps={{
                    startAdornment: renderStartAdornment(),
                    endAdornment: renderEndAdornment(),
                    ...(showCountryPicker && { 'aria-label': 'Country code' }),
                }}
                slotProps={{
                    htmlInput: {
                        maxLength,
                        'aria-label': label || title || placeholder,
                    },
                }}
                onBlur={onBlur}
                sx={sx}
                fullWidth
                className="phoneTextFiled"
            />

            {errorValue && <TextFieldError errorValue={errorValue} />}
            {helperTitle && (
                <Box onClick={helperTitleOnClick} sx={{ cursor: 'pointer' }}>
                    <Typography variant="subtitle2" className="helperTitle">
                        {helperTitle}
                    </Typography>
                </Box>
            )}
        </TextFieldWrapper>
    );
};
